/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { SourceType } from 'client-code/channels-service';

import { DEFAULT_RETRY_OPTIONS } from '../../../config';
import { OneLineGuideCell } from '../../../core/entities';
import { Channel } from '../../../core/entities/Channel';
import { GuideType, InteractiveComponents, SeparatorTypes } from '../../../core/enums';
import { TypedChannelsNotFoundError } from '../../../core/error';
import { ServicesClientImpl } from '../../../core/interactors';
import { Locale } from '../../../core/locale/Locale';
import { LocaleKeys } from '../../../core/locale/LocaleKeys';
import { Log, QoEAppLogger } from '../../../core/logging';
import { VuLogger } from '../../../core/logging/VULogger';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import { PlaybackContentType } from '../../../core/playback/enums';
import { PlaybackDataModelImpl } from '../../../core/playback/impls/PlaybackDataModelImpl';
import { RetryMechanism } from '../../../core/retrymechanism/RetryMechanism';
import { RetryOptions } from '../../../core/retrymechanism/RetryOptions';
import { Storage } from '../../../core/storage/Storage';
import type { TGetState, TivoPayloadAction } from '../../../core/tivo/tivo-redux-wrapper';
import { TChannels, TGridGuideRow, TGuideCell, TOverlayOptions } from '../../../core/types';
import { CcbSwitch } from '../../../core/utils/ccb';
import { CcbSwitchName } from '../../../core/utils/ccb/enums';
import { IPlayable } from '../../../core/videoplayer/interfaces';
import { VideoPlaybackController } from '../../../core/videoplayer/VideoPlaybackController';
import { WhisperTypes } from '../../../views/Whisper/WhisperTypes';
import {
    onRepaintFilteredGuideAction,
    resetToNullFilteredGuideLastRefetchTimestampAction,
} from '../../guide/filteredguide/filteredguide.actions';
import { resetFilteredGuideIndexes } from '../../guide/filteredguide/filteredguide.reducers';
import { setGridGuideOffsetAction } from '../../guide/guide.actions';
import { loadGridGuide } from '../../guide/guide.reducers';
import { setVerticalPageCount } from '../../guide/guidehighlight/guidehighlight.reducers';
import { launchVodScreen } from '../../jumpchannel/jumpchannel.reducers';
import {
    hideOverlayDialog,
    onKeyLeftOverlayDialog,
    onKeyRightOverlayDialog,
    showOverlayDialog,
} from '../../overlaydialog/overlaydialog.reducers';
import { IOverlayDialog } from '../../overlaydialog/overlaydialogprops';
import {
    setScreenTaskController,
    showEmptyFilteredGuideScreen,
    showFilteredGuideScreen,
    showGuideScreen,
} from '../../screens/screens.reducers';
import { startVideoPlayback } from '../../videoplayer/videoplayer.reducers';
import { TAG } from '../../videoplayerui/onelineguide/onelineguide.constants';
import {
    getOneLineGuideChannels,
    refreshOneLineGuideAfterChannelSettingsUpdate,
    showOneLineGuide,
} from '../../videoplayerui/onelineguide/onelineguide.reducers';
import { hideWhisper, showWhisper } from '../../whisper/whisper.reducer';
import { setOverlayHighlightVerticalPositionAction } from '../overlayhighlight/overlayhighlight.actions';
import { OverlayTypes } from '../OverlayTypes';
import {
    setChannelOptionsAction,
    setChannelOptionsOverlayContextAction,
    setChannelsTypeAction,
} from './channeloptionsoverlay.actions';
import {
    GuideChannelOptionsOverlayActions,
    OneLineGuideChannelOptionsOverlayActions,
} from './channeloptionsoverlay.enum';
import {
    RESET_CATEGORIES_FILTER,
    SET_CHANNEL_OPTIONS,
    SET_CHANNEL_OPTIONS_OVERLAY_CONTEXT,
    SET_CHANNELS_TYPE,
} from './channeloptionsoverlay.types';

const initialState: {
    channel: Channel | null;
    channelsTypeIndex: string;
    categoriesTypeIndex: string;
    channelsType: string;
    categoriesType: string;
    watchNowText: string;
    channelsText: string;
    categoriesText: string;
    guideType: GuideType;
    horizontalSelectionOptions: string[];
    horizontalCategoriesSelectionOptions: string[];
} = {
    channel: null,
    channelsTypeIndex:
        Storage.getInstance().getItem(Storage.Keys.CHANNELS_TYPE) ||
        LocaleKeys.GUIDE_CHANNEL_TYPES_ALL_CHANNELS,
    categoriesTypeIndex: LocaleKeys.GUIDE_CATEGORY_TYPES_ALL,
    channelsType: '',
    categoriesType: '',
    watchNowText: '',
    categoriesText: '',
    channelsText: '',
    guideType: GuideType.GRID_GUIDE,
    horizontalSelectionOptions: [],
    horizontalCategoriesSelectionOptions: [],
};
export const GLOBAL_MOUSE_TRAP: string = 'HideChannelOptionsOverlay';

/* TODO: FAVORITES:
 *
 *      After favorites feature is fully implemented transform
 *  mHorizontalSelectionOptionIndex back in a plain array.
 *
 *      For the moment we are converting it to a function because
 *  if we leave it as an object, the Favorites option will never
 *  be visible because the object is instantiated only once in the
 *  global scope when the CCBSwitch class is not yet initiated.
 *
 *      Because it is not initiated, it will return false for
 *  any CCB Switch flag.
 *
 *  */
const mHorizontalSelectionOptionIndex = () => [
    LocaleKeys.GUIDE_CHANNEL_TYPES_MY_CHANNELS,
    LocaleKeys.GUIDE_CHANNEL_TYPES_FAVORITES,
    LocaleKeys.GUIDE_CHANNEL_TYPES_ALL_CHANNELS,
];

const mHorizontalCategoriesSelectionOptionsIndex = () => [
    LocaleKeys.GUIDE_CATEGORY_TYPES_ALL,
    LocaleKeys.GUIDE_CATEGORY_TYPES_MOVIES,
    LocaleKeys.GUIDE_CATEGORY_TYPES_SPORTS,
    LocaleKeys.GUIDE_CATEGORY_TYPES_KIDS,
    LocaleKeys.GUIDE_CATEGORY_TYPES_NEWS,
];

const channelOptionsOverlayReducer = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<typeof initialState>>{},
) => {
    switch (type) {
        case SET_CHANNELS_TYPE:
            return {
                ...state,
                channelsType: payload.channelsType,
                channelsTypeIndex: payload.channelsTypeIndex,
                categoriesType: payload.categoriesType,
                categoriesTypeIndex: payload.categoriesTypeIndex,
            };
        case SET_CHANNEL_OPTIONS:
            return {
                ...state,
                channelsType: payload.channelsType,
                watchNowText: payload.watchNowText,
                channelsText: payload.channelsText,
                categoriesText: payload.categoriesText,
                horizontalSelectionOptions: payload.horizontalSelectionOptions,
                horizontalCategoriesSelectionOptions: payload.horizontalCategoriesSelectionOptions,
            };
        case SET_CHANNEL_OPTIONS_OVERLAY_CONTEXT:
            return {
                ...state,
                channel: payload.channel,
                guideType: payload.guideType,
            };
        case RESET_CATEGORIES_FILTER:
            return {
                ...state,
                categoriesType: Locale.getString(LocaleKeys[LocaleKeys.GUIDE_CATEGORY_TYPES_ALL]),
                categoriesTypeIndex: LocaleKeys.GUIDE_CATEGORY_TYPES_ALL,
            };
        default:
            return state;
    }
};

export const showChannelOptionsOverlay =
    (channel: Channel, guideType: GuideType) => (dispatch: Function, getState: TGetState) => {
        const { channelOptionsOverlay } = getState();
        const {
            channelsTypeIndex,
            categoriesTypeIndex,
            watchNowText,
            channelsText,
            categoriesText,
            horizontalSelectionOptions,
            horizontalCategoriesSelectionOptions,
        } = channelOptionsOverlay;

        const selectedChannelsTypeIndex =
            mHorizontalSelectionOptionIndex().indexOf(channelsTypeIndex);
        const selectedcategoriesTypeIndex =
            mHorizontalCategoriesSelectionOptionsIndex().indexOf(categoriesTypeIndex);

        const { channelNumber, affiliate, callSign, channelLogoUrl, isFavorite, sourceType } =
            channel;

        // TODO: Remove this check on completion of ENABLE_VOD_JUMP_CHANNEL_REMOVE_IN_KST_12189
        const isJumpChannelEnabled = CcbSwitch.getInstance().isEnabledAndOverriden(
            CcbSwitchName.ENABLE_VOD_JUMP_CHANNEL_REMOVE_IN_KST_12189,
        );
        const isJumpChannel = sourceType === SourceType.JumpChannel;
        const goToOnDemandText = Locale.getString(LocaleKeys.GO_TO_ON_DEMAND_TEXT);
        const addFavoriteChannelsText = Locale.getString(
            LocaleKeys.OVERLAY_OPTIONS_ADD_FAVORITE_CHANNELS_TEXT,
        );
        const removeFavoriteChannelsText = Locale.getString(
            LocaleKeys.OVERLAY_OPTIONS_REMOVE_FAVORITE_CHANNELS_TEXT,
        );
        const viewGuideWithTheseOptionsText = Locale.getString(
            LocaleKeys.OVERLAY_OPTIONS_VIEW_GUIDE_WITH_THESE_OPTIONS,
        );

        const favoriteChannelsText = isFavorite
            ? removeFavoriteChannelsText
            : addFavoriteChannelsText;

        const favoriteChannelsOption = {
            text: favoriteChannelsText,
        };

        const filterButtons = generateChannelOptionsOverlayFilteredButtons({
            channelsText,
            selectedChannelsTypeIndex,
            horizontalSelectionOptions,
            viewGuideWithTheseOptionsText,
            categoriesText,
            selectedcategoriesTypeIndex,
            horizontalCategoriesSelectionOptions,
        });

        const caretIndex = guideType === GuideType.ONE_LINE_GUIDE ? 3 : 2;

        const overlayDialogProps: IOverlayDialog = {
            channel,
            enableCaret: true,
            hasDimmer: true,
            hasSeperator: false,
            iconURL: channelLogoUrl,
            mouseTrapValue: GLOBAL_MOUSE_TRAP,
            options: [
                {
                    text: isJumpChannelEnabled && isJumpChannel ? goToOnDemandText : watchNowText,
                },
                ...(guideType === GuideType.ONE_LINE_GUIDE
                    ? [{ text: Locale.getString(LocaleKeys.OVERLAY_OPTIONS_GO_TO_GUIDE_TEXT) }]
                    : []),
                favoriteChannelsOption,
                ...filterButtons,
            ],
            overlayType: OverlayTypes.CHANNEL_OPTIONS,
            subtitle: `${channelNumber} ${affiliate || callSign}`,
            title: Locale.getString(LocaleKeys.GUIDE_CHANNEL_OPTIONS_OVERLAY_TITLE),
            verticalPositionCaretAvailable: [caretIndex],
        };

        dispatch(setChannelOptionsOverlayContextAction(channel, guideType));
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY));
        dispatch(showOverlayDialog(overlayDialogProps));
    };

export const showGuideOptionsOverlay =
    (guideType: GuideType) => (dispatch: Function, getState: TGetState) => {
        const {
            channelsTypeIndex,
            channelsText,
            horizontalSelectionOptions,
            categoriesTypeIndex,
            categoriesText,
            horizontalCategoriesSelectionOptions,
        } = getState().channelOptionsOverlay;

        const selectedChannelsTypeIndex =
            mHorizontalSelectionOptionIndex().indexOf(channelsTypeIndex);
        const selectedcategoriesTypeIndex =
            mHorizontalCategoriesSelectionOptionsIndex().indexOf(categoriesTypeIndex);

        const viewGuideWithTheseOptionsText = Locale.getString(
            LocaleKeys.OVERLAY_OPTIONS_VIEW_GUIDE_WITH_THESE_OPTIONS,
        );

        const filterButtons = generateChannelOptionsOverlayFilteredButtons({
            channelsText,
            selectedChannelsTypeIndex,
            horizontalSelectionOptions,
            viewGuideWithTheseOptionsText,
            categoriesText,
            selectedcategoriesTypeIndex,
            horizontalCategoriesSelectionOptions,
        });

        const overlayDialogProps: IOverlayDialog = {
            enableCaret: true,
            hasDimmer: true,
            hasSeperator: false,
            mouseTrapValue: GLOBAL_MOUSE_TRAP,
            options: [
                ...(guideType === GuideType.ONE_LINE_GUIDE
                    ? [{ text: Locale.getString(LocaleKeys.OVERLAY_OPTIONS_GO_TO_GUIDE_TEXT) }]
                    : []),
                ...filterButtons,
            ],
            overlayType: OverlayTypes.CHANNEL_OPTIONS,
            title: Locale.getString(LocaleKeys.GUIDE_OPTIONS_OVERLAY_TITLE),
            verticalPositionCaretAvailable: guideType === GuideType.ONE_LINE_GUIDE ? [1] : [0],
        };

        dispatch(setScreenTaskController(ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY));
        dispatch(showOverlayDialog(overlayDialogProps));
    };

export const onKeyOkChannelOptionsOverlayGuide =
    () => (dispatch: Function, getState: TGetState) => {
        const { verticalPosition: overlayHighlightVerticalPosition } = getState().overlayHighlight;

        if (overlayHighlightVerticalPosition === GuideChannelOptionsOverlayActions.WATCH_NOW) {
            dispatch(channelOptionsOverlayWatchNow());
        } else if (
            overlayHighlightVerticalPosition ===
            GuideChannelOptionsOverlayActions.FAVORITE_CHANNEL_ADD_REMOVE
        ) {
            dispatch(channelOptionsOverlayAddRemoveFavoriteChannel(GuideType.GRID_GUIDE));
        } // #region Delete this when flag ENABLE_FILTERED_GUIDE_REMOVE_IN_KST_12294 is removed
        else if (
            !CcbSwitch.getInstance().isEnabledAndOverriden(
                CcbSwitchName.ENABLE_FILTERED_GUIDE_REMOVE_IN_KST_12294,
            )
        ) {
            dispatch(
                channelOptionsOverlayApplyFilter(
                    0,
                    // -2 because apply filters and categories fields are missing
                    GuideChannelOptionsOverlayActions.CHANNELS_FILTER_PILL - 2,
                ),
            );
        }
        // #endregion
        else if (
            overlayHighlightVerticalPosition ===
            GuideChannelOptionsOverlayActions.VIEW_GUIDE_WITH_SELECTED_FILTERS
        ) {
            dispatch(
                channelOptionsOverlayApplyFilter(
                    GuideChannelOptionsOverlayActions.CATEOGORIES_FILTER_PILL,
                    GuideChannelOptionsOverlayActions.CHANNELS_FILTER_PILL,
                ),
            );
        } else {
            dispatch(
                setOverlayHighlightVerticalPositionAction(
                    GuideChannelOptionsOverlayActions.VIEW_GUIDE_WITH_SELECTED_FILTERS,
                ),
            );
        }
    };

export const onKeyOkChannelOptionsOverlayOneLineGuide =
    () => (dispatch: Function, getState: TGetState) => {
        const { verticalPosition: overlayHighlightVerticalPosition, horizontalPosition } =
            getState().overlayHighlight;

        const isFilteredGuideCCBActive = CcbSwitch.getInstance().isEnabledAndOverriden(
            CcbSwitchName.ENABLE_FILTERED_GUIDE_REMOVE_IN_KST_12294,
        );

        if (
            overlayHighlightVerticalPosition === OneLineGuideChannelOptionsOverlayActions.WATCH_NOW
        ) {
            dispatch(channelOptionsOverlayWatchNow(true));
        } else if (
            overlayHighlightVerticalPosition ===
            OneLineGuideChannelOptionsOverlayActions.GO_TO_GRID_GUIDE
        ) {
            dispatch(showGuideScreen());
        } else if (
            overlayHighlightVerticalPosition ===
            OneLineGuideChannelOptionsOverlayActions.FAVORITE_CHANNEL_ADD_REMOVE
        ) {
            dispatch(channelOptionsOverlayAddRemoveFavoriteChannel(GuideType.ONE_LINE_GUIDE));
            dispatch(showOneLineGuide());
        } // #region Delete this when flag ENABLE_FILTERED_GUIDE_REMOVE_IN_KST_12294 is removed
        else if (!isFilteredGuideCCBActive) {
            dispatch(
                channelOptionsOverlayApplyFilter(
                    0,
                    // -2 because apply filters and categories fields are missing
                    // when flag is not active
                    OneLineGuideChannelOptionsOverlayActions.CHANNELS_FILTER_PILL - 2,
                ),
            );
            dispatch(showOneLineGuide());
        }
        // #endregion
        else if (
            overlayHighlightVerticalPosition ===
            OneLineGuideChannelOptionsOverlayActions.VIEW_GUIDE_WITH_SELECTED_FILTERS
        ) {
            dispatch(
                channelOptionsOverlayApplyFilter(
                    OneLineGuideChannelOptionsOverlayActions.CATEOGORIES_FILTER_PILL,
                    OneLineGuideChannelOptionsOverlayActions.CHANNELS_FILTER_PILL,
                ),
            );
            if (
                userHasSelectedCategoryFilter(
                    OneLineGuideChannelOptionsOverlayActions.CATEOGORIES_FILTER_PILL,
                    horizontalPosition,
                )
            ) {
                return;
            }
            dispatch(showOneLineGuide());
        } else {
            dispatch(
                setOverlayHighlightVerticalPositionAction(
                    OneLineGuideChannelOptionsOverlayActions.VIEW_GUIDE_WITH_SELECTED_FILTERS,
                ),
            );
        }
    };

export const onKeyPlayChannelOptionsOverlay = () => (dispatch: Function, getState: TGetState) => {
    const { verticalPosition: overlayHighlightVerticalPosition } = getState().overlayHighlight;
    if (overlayHighlightVerticalPosition > 0) return;
    dispatch(onKeyOkChannelOptionsOverlayGuide());
};

export const onKeyBackChannelOptionsOverlay = () => (dispatch: Function) => {
    VuLogger.logChannelOptionOverlayCancelUserAction();
    dispatch(hideOverlayDialog());
};

export const setChannelOptions = () => (dispatch: Function) => {
    const watchNowText = Locale.getString(LocaleKeys.OVERLAY_OPTIONS_WATCH_NOW_TEXT);
    const channelsText = Locale.getString(LocaleKeys.OVERLAY_OPTIONS_CHANNELS_TEXT);
    const categoriesText = Locale.getString(LocaleKeys.OVERLAY_OPTIONS_CATEGORIES_TEXT);

    const horizontalSelectionOptions = mHorizontalSelectionOptionIndex().map(Locale.getString);
    const horizontalCategoriesSelectionOptions = mHorizontalCategoriesSelectionOptionsIndex().map(
        Locale.getString,
    );
    const mChannelsType =
        Locale.getString(Storage.getInstance().getItem(Storage.Keys.CHANNELS_TYPE)) ||
        Locale.getString(LocaleKeys.GUIDE_CHANNEL_TYPES_MY_CHANNELS);
    const mCategoriesType = Locale.getString(LocaleKeys.GUIDE_CATEGORY_TYPES_ALL);

    dispatch(
        setChannelOptionsAction(
            mChannelsType,
            mCategoriesType,
            watchNowText,
            channelsText,
            categoriesText,
            horizontalSelectionOptions,
            horizontalCategoriesSelectionOptions,
        ),
    );
};

export const onMouseClickChannelOptionsOverlay =
    () => (dispatch: Function, getState: TGetState) => {
        const { activeComponent } = getState().interactiveLayer;
        const { value } = getState().inputFactory;
        const { guideType } = getState().channelOptionsOverlay;

        if (value === GLOBAL_MOUSE_TRAP) {
            dispatch(hideOverlayDialog());
            return;
        }

        if (activeComponent === InteractiveComponents.WEST) dispatch(onKeyLeftOverlayDialog());
        else if (activeComponent === InteractiveComponents.EAST)
            dispatch(onKeyRightOverlayDialog());
        else if (guideType === GuideType.ONE_LINE_GUIDE)
            dispatch(onKeyOkChannelOptionsOverlayOneLineGuide());
        else dispatch(onKeyOkChannelOptionsOverlayGuide());
    };

const getGuideCells = (getState: TGetState): TGuideCell[] => {
    const { guideType } = getState().channelOptionsOverlay;
    let guideCells: TGuideCell[] = [];
    if (guideType === GuideType.GRID_GUIDE) {
        const { gridGuide, guideHighlight } = getState();
        const { verticalPosition } = guideHighlight;
        const { guideRows, isPastGuideMode, futureGuideRows } = gridGuide;
        const guideRowsData: TGridGuideRow[] = isPastGuideMode ? futureGuideRows : guideRows;
        guideCells = guideRowsData[verticalPosition]?.guideCells;
    }

    if (guideType === GuideType.ONE_LINE_GUIDE) {
        const { cellList } = getState().oneLineGuide;
        guideCells = cellList.map(
            (cell: OneLineGuideCell): TGuideCell => ({
                ...cell,
            }),
        );
    }

    return guideCells;
};

const channelOptionsOverlayWatchNow =
    (isOLG: boolean = false) =>
    (dispatch: Function, getState: TGetState) => {
        const { channel } = getState().channelOptionsOverlay;

        if (!channel) {
            Log.e(TAG, 'Channel is null in channelOptionsOverlayWatchNow');
            return;
        }

        // TODO: Remove this check on completion of ENABLE_VOD_JUMP_CHANNEL_REMOVE_IN_KST_12189
        const isJumpChannelEnabled = CcbSwitch.getInstance().isEnabledAndOverriden(
            CcbSwitchName.ENABLE_VOD_JUMP_CHANNEL_REMOVE_IN_KST_12189,
        );
        const isJumpChannel = channel.sourceType === SourceType.JumpChannel;
        if (isJumpChannelEnabled && channel.channelNumber && isJumpChannel) {
            dispatch(launchVodScreen(channel.channelNumber));
            Log.i(TAG, 'Navigating to VOD screen for Jump Channel');
            dispatch(hideOverlayDialog());
            return;
        }

        const guideCells = getGuideCells(getState);
        const services = new ServicesClientImpl();
        services.getVuLoggerProperties(channel, guideCells).then((properties) => {
            VuLogger.logItemSelectedEvent(properties);
        });

        if (channel.channelNumber !== QoEAppLogger.lastTunedChannel) {
            QoEAppLogger.setWatchContentEndReason(
                QoEAppLogger.PlaybackEndReason
                    .USER_MOVE_TO_LIVE_TV_FROM_GUIDE_AND_CHANNEL_WAS_CHANGED,
            );
        }
        if (
            channel.channelNumber !==
            VideoPlaybackController.getInstance().getPlayable()?.channel?.channelNumber
        ) {
            VuLogger.logWatchStoppedEvent(VuLogger.WatchStoppedEndReason.CHANNEL_CHANGE);
        }
        dispatch(hideOverlayDialog());
        const playbackModelImpl = PlaybackDataModelImpl.getInstance();
        if (!VideoPlaybackController.getInstance().isLive()) {
            VideoPlaybackController.getInstance().updateLastViewedPosition();
            playbackModelImpl.resetPlaybackData();
        }

        const livePlayable: IPlayable = {
            channel,
            playbackContentType: PlaybackContentType.LIVE,
        };

        if (isOLG) {
            dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS));
            VideoPlaybackController.getInstance().startPlayback(livePlayable);
        } else dispatch(startVideoPlayback(livePlayable));
    };

export const channelOptionsOverlayAddRemoveFavoriteChannel =
    (guideType: GuideType) => (dispatch: Function, getState: Function) => {
        const { channel, channelsTypeIndex } = getState().channelOptionsOverlay;
        const { selectedChannel } = getState().channelOptionsOverlay;

        if (channel) {
            const services: ServicesClientImpl = new ServicesClientImpl();
            const setChannelSettings = services.setChannelSettings.bind(services);

            dispatch(hideWhisper());

            RetryMechanism.callServiceWithRetry<boolean>(
                setChannelSettings,
                [channel, !channel.isFavorite],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            ).then((response: boolean) => {
                if (!response) {
                    dispatch(
                        showWhisper({
                            message: Locale.getString(
                                LocaleKeys.CHANNEL_SETTINGS_ERROR_WHISPER_MESSAGE,
                            ),
                            iconType: WhisperTypes.INFO,
                        }),
                    );
                    return;
                }

                dispatch(displayWhisperForActionSuccess());

                services.updateFavoriteChannel(channel, !channel.isFavorite).then(() => {
                    channel.isFavorite = !channel.isFavorite;

                    if (selectedChannel && selectedChannel.channelId === channel.channelId) {
                        selectedChannel.isFavorite = channel.isFavorite;
                    }

                    if (channelsTypeIndex !== LocaleKeys.GUIDE_CHANNEL_TYPES_FAVORITES) return;

                    if (guideType === GuideType.GRID_GUIDE) dispatch(loadGridGuide());
                    else if (guideType === GuideType.ONE_LINE_GUIDE)
                        dispatch(refreshOneLineGuideAfterChannelSettingsUpdate(channel.isFavorite));
                    else if (guideType === GuideType.FILTERED_GUIDE) {
                        setTimeout(() => {
                            dispatch(resetFilteredGuideIndexes());
                            dispatch(onRepaintFilteredGuideAction());
                        }, 500);
                    }
                });
            });
        }

        dispatch(hideOverlayDialog());
    };

const displayWhisperForActionSuccess = () => (dispatch: Function, getState: TGetState) => {
    const { channel } = getState().channelOptionsOverlay;
    const channelNameShort = 'CHANNEL_SHORT_NAME';

    const channelText = `${channel?.channelNumber} (${channel?.callSign})`;
    const whisperMessage = channel?.isFavorite
        ? Locale.getString(LocaleKeys.CHANNEL_SETTINGS_REMOVE_WHISPER_MESSAGE).replace(
              channelNameShort,
              channelText,
          )
        : Locale.getString(LocaleKeys.CHANNEL_SETTINGS_ADD_WHISPER_MESSAGE).replace(
              channelNameShort,
              channelText,
          );

    dispatch(
        showWhisper({
            message: whisperMessage,
            iconType: WhisperTypes.INFO,
        }),
    );
};

export const channelOptionsOverlayApplyFilter =
    (categoriesFilterIndex: number, channelFilterIndex: number) =>
    (dispatch: Function, getState: Function) => {
        const { horizontalPosition: overlayHighlightHorizontalPosition } =
            getState().overlayHighlight;

        const { channel, channelsTypeIndex, categoriesTypeIndex, guideType } =
            getState().channelOptionsOverlay;

        const isFilteredGuideCCBActive = CcbSwitch.getInstance().isEnabledAndOverriden(
            CcbSwitchName.ENABLE_FILTERED_GUIDE_REMOVE_IN_KST_12294,
        );

        const selectedChannelsTypeIndex =
            mHorizontalSelectionOptionIndex()[
                overlayHighlightHorizontalPosition[channelFilterIndex]
            ];

        const selectedCategoriesTypeIndex = isFilteredGuideCCBActive
            ? mHorizontalCategoriesSelectionOptionsIndex()[
                  overlayHighlightHorizontalPosition[categoriesFilterIndex]
              ]
            : LocaleKeys.GUIDE_CATEGORY_TYPES_ALL;

        if (
            channelsTypeIndex === selectedChannelsTypeIndex &&
            categoriesTypeIndex === selectedCategoriesTypeIndex
        ) {
            dispatch(hideOverlayDialog());
            return;
        }

        VuLogger.logChannelOptionOverlayOkUserAction(
            `${selectedChannelsTypeIndex} + ${selectedCategoriesTypeIndex}`,
        );
        Storage.getInstance().setItem(Storage.Keys.CHANNELS_TYPE, selectedChannelsTypeIndex);

        dispatch(
            setChannelsTypeAction(
                Locale.getString(LocaleKeys[selectedChannelsTypeIndex]),
                Locale.getString(LocaleKeys[selectedCategoriesTypeIndex]),
                selectedChannelsTypeIndex,
                selectedCategoriesTypeIndex,
            ),
        );

        if (
            isFilteredGuideCCBActive &&
            LocaleKeys.GUIDE_CATEGORY_TYPES_ALL !== selectedCategoriesTypeIndex
        ) {
            dispatch(hideOverlayDialog());
            dispatch(showFilteredGuideAndCheckForEmpty());
            return;
        }
        if (guideType === GuideType.GRID_GUIDE) {
            dispatch(setVerticalPageCount(0));
            dispatch(setGridGuideOffsetAction(0));
            dispatch(loadGridGuide());
        }
        dispatch(getOneLineGuideChannels(channel, true));
        dispatch(hideOverlayDialog());
    };

export const userHasSelectedCategoryFilter = (
    categoriesFilterIndex: number,
    overlayHighlightHorizontalPosition: number[],
) => {
    // #region Delete this when flag ENABLE_FILTERED_GUIDE_REMOVE_IN_KST_12294 is removed
    const isFilteredGuideCCBActive = CcbSwitch.getInstance().isEnabledAndOverriden(
        CcbSwitchName.ENABLE_FILTERED_GUIDE_REMOVE_IN_KST_12294,
    );
    if (!isFilteredGuideCCBActive) {
        return false;
    }
    // #endregion
    const selectedCategoriesTypeIndex =
        mHorizontalCategoriesSelectionOptionsIndex()[
            overlayHighlightHorizontalPosition[categoriesFilterIndex]
        ];

    return LocaleKeys.GUIDE_CATEGORY_TYPES_ALL !== selectedCategoriesTypeIndex;
};

const showFilteredGuideAndCheckForEmpty = () => (dispatch: Function, getState: Function) => {
    const { channelsType } = getState().channelOptionsOverlay;
    const favorites = channelsType === Locale.getString(LocaleKeys.GUIDE_CHANNEL_TYPES_FAVORITES);

    dispatch(resetToNullFilteredGuideLastRefetchTimestampAction());
    dispatch(resetFilteredGuideIndexes());

    if (!favorites) {
        dispatch(showFilteredGuideScreen());
        return;
    }

    const services = new ServicesClientImpl();
    const fetchChannels = services.fetchChannels.bind(services);
    RetryMechanism.callServiceWithRetry<TChannels>(
        fetchChannels,
        [channelsType],
        <RetryOptions>DEFAULT_RETRY_OPTIONS,
    )
        .then(() => {
            dispatch(showFilteredGuideScreen());
        })
        .catch((error) => {
            if (error instanceof TypedChannelsNotFoundError) {
                if (favorites) {
                    dispatch(showEmptyFilteredGuideScreen());
                    return;
                }
            }
            Diagnostics.error(
                TAG,
                'Error while fetching channels Failed to get guide FilteredGuide.showFilteredGuideAndCheckForEmpty() -> services.fetchChannels',
            );
            Diagnostics.error(TAG, 'stacktrace:', error);
            Log.e(TAG, error);
        });
};

const generateChannelOptionsOverlayFilteredButtons = ({
    channelsText,
    selectedChannelsTypeIndex,
    horizontalSelectionOptions,
    viewGuideWithTheseOptionsText,
    categoriesText,
    selectedcategoriesTypeIndex,
    horizontalCategoriesSelectionOptions,
}: {
    channelsText: string;
    selectedChannelsTypeIndex: number;
    horizontalSelectionOptions: string[];
    viewGuideWithTheseOptionsText: string;
    categoriesText: string;
    selectedcategoriesTypeIndex: number;
    horizontalCategoriesSelectionOptions: string[];
}): TOverlayOptions[] => {
    const isFilteredGuideCCBActive = CcbSwitch.getInstance().isEnabledAndOverriden(
        CcbSwitchName.ENABLE_FILTERED_GUIDE_REMOVE_IN_KST_12294,
    );
    if (!isFilteredGuideCCBActive) {
        return [
            {
                text: channelsText,
                horizontalSelectionoptions: horizontalSelectionOptions,
                initialHorizontalIndex: selectedChannelsTypeIndex,
                separatorType: SeparatorTypes.DEFAULT,
                hrTop: true,
            },
        ];
    }

    return [
        { text: viewGuideWithTheseOptionsText, hrTop: true },
        {
            text: categoriesText,
            horizontalSelectionoptions: horizontalCategoriesSelectionOptions,
            initialHorizontalIndex: selectedcategoriesTypeIndex,
            separatorType: SeparatorTypes.DEFAULT,
            marginLeft: true,
        },
        {
            text: channelsText,
            horizontalSelectionoptions: horizontalSelectionOptions,
            initialHorizontalIndex: selectedChannelsTypeIndex,
            separatorType: SeparatorTypes.DEFAULT,
            marginLeft: true,
        },
    ];
};

export default channelOptionsOverlayReducer;
